import { gql } from "@apollo/client";

// Vendor picker options. The page is admin-only, so the full list is fine.
export const GET_VENDOR_OPTIONS = gql`
  query MergeVendorOptions {
    vendors(order_by: { name: asc }) {
      id
      name
      is_active
      organization {
        id
        name
      }
    }
  }
`;

// Everything owned by the two vendors: records, locations (with their menu
// links), menus (with their item links), items, and clerk accounts.
//
// menu.vendor_id / location.vendor_id are selected on the JOINED side too so
// the page can flag cross-vendor tangles (e.g. a menu owned by vendor A
// attached to a location owned by vendor B).
export const GET_MERGE_PREVIEW = gql`
  query MergeVendorPreview($src: Int!, $tgt: Int!) {
    source: vendors_by_pk(id: $src) {
      ...VendorRecord
    }
    target: vendors_by_pk(id: $tgt) {
      ...VendorRecord
    }
    srcLocations: locations(
      where: { vendor_id: { _eq: $src } }
      order_by: { name: asc }
    ) {
      ...LocationRow
    }
    tgtLocations: locations(
      where: { vendor_id: { _eq: $tgt } }
      order_by: { name: asc }
    ) {
      ...LocationRow
    }
    srcMenus: menus(
      where: { vendor_id: { _eq: $src } }
      order_by: { name: asc }
    ) {
      ...MenuRow
    }
    tgtMenus: menus(
      where: { vendor_id: { _eq: $tgt } }
      order_by: { name: asc }
    ) {
      ...MenuRow
    }
    srcItems: items(
      where: { vendor_id: { _eq: $src } }
      order_by: { name: asc }
    ) {
      ...ItemRow
    }
    tgtItems: items(
      where: { vendor_id: { _eq: $tgt } }
      order_by: { name: asc }
    ) {
      ...ItemRow
    }
    srcUsers: users_aggregate(where: { vendor_id: { _eq: $src } }) {
      aggregate {
        count
      }
    }
    tgtUsers: users_aggregate(where: { vendor_id: { _eq: $tgt } }) {
      aggregate {
        count
      }
    }
  }

  fragment VendorRecord on vendors {
    id
    name
    type
    other_type
    poc_name
    poc_email
    poc_phone
    revenue_share
    flat_fee
    sales_tax_responsible
    is_active
    created_at
    organization {
      id
      name
    }
  }

  fragment LocationRow on locations {
    id
    name
    is_active
    event {
      id
      name
    }
    location_menus {
      menu {
        id
        name
        is_active
        vendor_id
      }
    }
  }

  fragment MenuRow on menus {
    id
    name
    is_active
    event {
      id
      name
    }
    menu_items_aggregate {
      aggregate {
        count
      }
    }
    location_menus {
      location {
        id
        name
        vendor_id
      }
    }
  }

  fragment ItemRow on items {
    id
    name
    price
    is_active
    event {
      id
      name
    }
    menu_items {
      menu_id
    }
  }
`;

// Financial rollups, keyed off each vendor's LOCATION ids, not vendor_id:
// orders.vendor_id is unindexed and a count by it gets killed on the read
// replica (>60s full scan), while location_id resolves in under a second.
// payments/transactions have no vendor_id at all — they only reach a vendor
// through their order. An empty $locIds list correctly matches nothing.
export const GET_FINANCIAL_ROLLUP = gql`
  query MergeVendorFinancials($srcLocIds: [Int!]!, $tgtLocIds: [Int!]!) {
    srcOrders: orders_aggregate(where: { location_id: { _in: $srcLocIds } }) {
      aggregate {
        count
        sum {
          subtotal
          tax
          tip
        }
      }
    }
    tgtOrders: orders_aggregate(where: { location_id: { _in: $tgtLocIds } }) {
      aggregate {
        count
        sum {
          subtotal
          tax
          tip
        }
      }
    }
    srcTransactions: transactions_aggregate(
      where: { order: { location_id: { _in: $srcLocIds } } }
    ) {
      aggregate {
        count
        sum {
          amount
        }
      }
    }
    tgtTransactions: transactions_aggregate(
      where: { order: { location_id: { _in: $tgtLocIds } } }
    ) {
      aggregate {
        count
        sum {
          amount
        }
      }
    }
    srcPayments: payments_aggregate(
      where: { transactions: { order: { location_id: { _in: $srcLocIds } } } }
    ) {
      aggregate {
        count
        sum {
          amount
        }
      }
    }
    tgtPayments: payments_aggregate(
      where: { transactions: { order: { location_id: { _in: $tgtLocIds } } } }
    ) {
      aggregate {
        count
        sum {
          amount
        }
      }
    }
  }
`;
